import { useEffect, useState } from "react";
import { AlertCircle, Materials, Plus, Stedenbouw } from "assets/icons";
import ButtonNew from "components/buttonNew/buttonNew";
import { CardBuildingShell } from "./buildingShellCard";
import { MainOrAnnex, RoofDto } from "shared/types/roof";
import { toast } from "react-toastify";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import ModalRoof from "./modalRoof";
import ModalWindow from "./modalWindow";
import { WindowDto, WindowLocation } from "shared/types/window";
import ModalFacade from "./modalFacade";
import { FacadeDto, FacadePosition } from "shared/types/facade";
import { FileLabel } from "shared/types/file";
interface Building {
  id?: string | null;
}

export const BuildingShellTab = ({ building }: { building: Building }) => {
  const [roofs, setRoofs] = useState<RoofDto[]>([]);
  const [windows, setWindows] = useState<WindowDto[]>([]);
  const [facades, setFacades] = useState<FacadeDto[]>([]);

  const [selectedRoof, setSelectedRoof] = useState<RoofDto | undefined>(
    undefined,
  );
  const [isRoofModalOpen, setIsRoofModalOpen] = useState(false);

  const [selectedWindow, setSelectedWindow] = useState<WindowDto | undefined>(
    undefined,
  );
  const [isWindowModalOpen, setIsWindowModalOpen] = useState(false);

  const [selectedFacade, setSelectedFacade] = useState<FacadeDto | undefined>(
    undefined,
  );
  const [isFacadeModalOpen, setIsFacadeModalOpen] = useState(false);

  const { session } = useSession();

  useEffect(() => {
    refreshRoofs();
  }, [building.id]);

  useEffect(() => {
    refreshWindows();
  }, [building.id]);

  useEffect(() => {
    refreshFacades();
  }, [building.id]);

  const handleOpenRoofModal = () => {
    setIsRoofModalOpen(true);
  };

  const handleCloseRoofModal = () => {
    setSelectedRoof(undefined);
    setIsRoofModalOpen(false);
  };

  const handleOpenWindowModal = () => {
    setIsWindowModalOpen(true);
  };

  const handleCloseWindowModal = () => {
    setSelectedWindow(undefined);
    setIsWindowModalOpen(false);
  };

  const handleOpenFacadeModal = () => {
    setIsFacadeModalOpen(true);
  };

  const handleCloseFacadeModal = () => {
    setSelectedFacade(undefined);
    setIsFacadeModalOpen(false);
  };

  const fetchRoofs = async (): Promise<RoofDto[]> => {
    console.log(`Frontend: Fetching roofs for building ID: ${building.id}`);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/building/${building.id}/roofs`,
        { method: "GET" },
        session,
      );
      console.log(
        `Frontend: Received response with status: ${response.status}`,
      );
      if (response.ok) {
        const roofs = await response.json();
        console.log(`Frontend: Fetched ${roofs.length} roofs`);
        return roofs;
      } else {
        console.error(
          `Frontend: Failed to fetch roofs, status code: ${response.status}`,
        );
        toast("Failed to fetch roofs. Please try again.");
      }
    } catch (error) {
      console.error("Frontend: Error fetching roofs: ", error);
      toast("Error fetching roofs!");
    }
    return [];
  };

  const refreshRoofs = async () => {
    const updatedRoofs = await fetchRoofs();
    setRoofs(updatedRoofs);
  };

  const fetchWindows = async (): Promise<WindowDto[]> => {
    console.log(`Frontend: Fetching windows for building ID: ${building.id}`);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/building/${building.id}/windows`,
        { method: "GET" },
        session,
      );
      console.log(
        `Frontend: Received response with status: ${response.status}`,
      );
      if (response.ok) {
        const windows = await response.json();
        console.log(`Frontend: Fetched ${windows.length} windows`);
        return windows;
      } else {
        console.error(
          `Frontend: Failed to fetch windows, status code: ${response.status}`,
        );
        toast("Failed to fetch windows. Please try again.");
      }
    } catch (error) {
      console.error("Frontend: Error fetching windows: ", error);
      toast("Error fetching windows!");
    }
    return [];
  };

  const refreshWindows = async () => {
    const updatedWindows = await fetchWindows();
    setWindows(updatedWindows);
  };

  const fetchFacades = async (): Promise<FacadeDto[]> => {
    console.log(`Frontend: Fetching facades for building ID: ${building.id}`);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/building/${building.id}/facades`,
        { method: "GET" },
        session,
      );
      console.log(
        `Frontend: Received response with status: ${response.status}`,
      );
      if (response.ok) {
        const facades = await response.json();
        console.log(`Frontend: Fetched ${facades.length} facades`);
        return facades;
      } else {
        console.error(
          `Frontend: Failed to fetch facades, status code: ${response.status}`,
        );
        toast("Failed to fetch facades. Please try again.");
      }
    } catch (error) {
      console.error("Frontend: Error fetching facades: ", error);
      toast("Error fetching facades!");
    }
    return [];
  };

  const refreshFacades = async () => {
    const updatedFacades = await fetchFacades();
    setFacades(updatedFacades);
  };

  return (
    <div className="grid grid-cols-1 gap-8">
      <div className="col-span-full flex flex-col gap-3">
        <header className="flex justify-between">
          <div className="flex items-center gap-2">
            <Materials className="h-[18px] w-[18px]" />
            <p className="text-lg">Daken</p>
          </div>
          <ButtonNew
            onClick={handleOpenRoofModal}
            icon={<Plus />}
            label="Dak toevoegen"
            variant="primaryMoss"
          />
        </header>
        <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:gap-4">
          {roofs.map((roof, index) => (
            <CardBuildingShell
              key={index}
              building={building}
              data={{
                title: roof.mainOrAnnex + ": " + roof.type + " " + roof.subtype, // Combining type and subtype for a descriptive title
                status: roof.status,
                sharePointPicturesFolderId: roof.sharePointPicturesFolderId,
                fileLabel: roof.mainOrAnnex == MainOrAnnex.MAIN ? FileLabel.MainRoofPicture : FileLabel.SideRoofPicture,
                sharePointPicturesFolderUrl: roof.sharePointPicturesFolderUrl,
                installer: roof.installer,
                nextInspectionDate: roof.nextInspectionDate,
                additionalInfo: [
                  { label: "Dakskelet", value: roof.skeleton },
                  { label: "Oriëntatie", value: roof.orientation },
                  { label: "Bedekking", value: roof.covering },
                  { label: "Oppervlakte", value: roof.surfaceArea ? `${roof.surfaceArea } m²` : "Niet ingevuld"},
                  
                  // Add other relevant fields here
                ],
              }}
              onOpenModal={() => {
                setSelectedRoof(roof);
                handleOpenRoofModal();
              }}
            />
          ))}
        </div>
      </div>
      <div className="col-span-full flex flex-col gap-3">
        <header className="flex justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-[18px] w-[18px]" />
            <p className="text-lg">Ramen</p>
          </div>
          <ButtonNew
            onClick={handleOpenWindowModal}
            icon={<Plus />}
            label="Raam toevoegen"
            variant="primaryMoss"
          />
        </header>
        <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:gap-4">
          {windows.map((window, index) => (
            <CardBuildingShell
              key={index}
              building={building}
              data={{
                title: window.type + " - " + window.location,
                status: window.status,
                sharePointPicturesFolderId: window.sharePointPicturesFolderId,
                fileLabel: window.location == WindowLocation.FRONT ? FileLabel.WindowsFrontPicture : window.location == WindowLocation.BACK ? FileLabel.WindowsBackPicture : window.location == WindowLocation.SIDE_LEFT ? FileLabel.WindowsLeftPicture : FileLabel.WindowsRightPicture, 
                sharePointPicturesFolderUrl: window.sharePointPicturesFolderUrl,
                installer: window.installer,
                nextInspectionDate: window.nextInspectionDate,
                additionalInfo: [
                  { label: "Materiaal", value: window.material },
                  { label: "Profiel kleur", value: window.profileColor },
                  { label: "Glasdikte", value: window.glassThickness ? `${window.glassThickness} mm` : "Niet ingevuld" },
                  // Add other relevant fields here
                ],
              }}
              onOpenModal={() => {
                setSelectedWindow(window);
                handleOpenWindowModal();
              }}
            />
          ))}
        </div>
      </div>
      <div className="col-span-full flex flex-col gap-3">
        <header className="flex justify-between">
          <div className="flex items-center gap-2">
            <Stedenbouw className="h-[18px] w-[18px]" />
            <p className="text-lg">Gevels</p>
          </div>
          <ButtonNew
            onClick={handleOpenFacadeModal}
            icon={<Plus />}
            label="Gevel toevoegen"
            variant="primaryMoss"
          />
        </header>
        <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:gap-4">
          {facades.map((facade, index) => (
            <CardBuildingShell
              key={index}
              building={building}
              data={{
                title: facade.facadePosition,
                status: facade.status,
                fileLabel: facade.facadePosition == FacadePosition.FRONT ? FileLabel.FrontagePicture : facade.facadePosition == FacadePosition.BACK ? FileLabel.BackFacadePicture : facade.facadePosition == FacadePosition.SIDE_LEFT ? FileLabel.SideFacadeLeftPicture : FileLabel.SideFacadeRightPicture,
                sharePointPicturesFolderId: facade.sharePointPicturesFolderId,
                sharePointPicturesFolderUrl: facade.sharePointPicturesFolderUrl,
                installer: facade.installer,
                nextInspectionDate: facade.nextInspectionDate,
                additionalInfo: [
                  { label: "Afwerking", value: facade.finishType },
                  { label: "Geïsoleerd", value: facade.insulated != null ? facade.insulated ? "Ja" : "Nee" : "Niet ingevuld"},
                  { label: "Oppervlakte", value: facade.grossArea ? `${facade.grossArea} m²` : "Niet ingevuld" },
                  // Add other relevant fields here
                ],
              }}
              onOpenModal={() => {
                setSelectedFacade(facade);
                handleOpenFacadeModal();
              }}
            />
          ))}
        </div>
      </div>
      <ModalRoof
        building={building}
        isOpen={isRoofModalOpen}
        onClose={handleCloseRoofModal}
        onRoofAdded={refreshRoofs}
        roof={selectedRoof}
      />
      <ModalWindow
        building={building}
        isOpen={isWindowModalOpen}
        onClose={handleCloseWindowModal}
        onWindowAdded={refreshWindows}
        window={selectedWindow}
      />
      <ModalFacade
        building={building}
        isOpen={isFacadeModalOpen}
        onClose={handleCloseFacadeModal}
        onFacadeAdded={refreshFacades}
        facade={selectedFacade}
      />
    </div>
  );
};

export default BuildingShellTab;
